<template>
  <div class="container mx-auto px-4 py-8 max-w-3xl">
    <section class="my-8">
      <h1 class="text-4xl font-bold font-orbitron mb-4">Start a project</h1>
      <p class="text-lg font-roboto text-gray-700 leading-relaxed">
        Tell us what you want to automate and what "done" would look like. We reply within two business days.
      </p>
      <p class="mt-4 font-roboto text-gray-700">
        You can also email us directly at
        <a :href="`mailto:${email}`" class="text-blue-600 hover:text-blue-800 underline">{{ email }}</a>.
      </p>
    </section>

    <div v-if="status" :class="status.ok ? 'border-green-600 bg-green-50' : 'border-red-600 bg-red-50'"
      class="rounded border-l-4 p-4 font-roboto">
      <p>{{ status.message }}</p>
      <p v-if="!status.ok" class="mt-2">
        <a :href="mailtoFallback" class="text-blue-600 hover:text-blue-800 underline">
          Send it by email instead
        </a>
        — this opens your mail client with the message already filled in.
      </p>
    </div>

    <form v-if="!status || !status.ok" @submit.prevent="submitForm" class="space-y-4 py-6">
      <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <input type="text" v-model="form.firstName" placeholder="First name*" class="input-field" required>
        <input type="text" v-model="form.lastName" placeholder="Last name*" class="input-field" required>
      </div>
      <input type="email" v-model="form.email" placeholder="Email*" class="input-field" required>
      <input type="text" v-model="form.subject" placeholder="Subject*" class="input-field" required>
      <textarea v-model="form.message" placeholder="What do you want to build or automate?*" rows="8"
        class="input-field" required></textarea>
      <button type="submit" class="btn-primary disabled:opacity-60" :disabled="submitting">
        {{ submitting ? 'Sending…' : 'Send message' }}
      </button>
    </form>

    <p class="text-sm font-roboto text-gray-600">
      This site is protected by reCAPTCHA and the Google
      <a href="https://policies.google.com/privacy" target="_blank" rel="noopener"
        class="text-blue-600 underline">Privacy Policy</a> and
      <a href="https://policies.google.com/terms" target="_blank" rel="noopener" class="text-blue-600 underline">Terms
        of Service</a> apply.
    </p>
  </div>
</template>

<script>
import { computed, reactive, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useReCaptcha } from 'vue-recaptcha-v3';
import axios from 'axios';
import site from '@/seo';

export default {
  name: 'ContactComponent',
  setup() {
    const route = useRoute();
    const recaptcha = useReCaptcha();

    const form = reactive({
      firstName: '',
      lastName: '',
      email: '',
      subject: route.query.service ? `${route.query.service} enquiry` : '',
      message: '',
    });
    const status = ref(null);
    const submitting = ref(false);

    const mailtoFallback = computed(() => {
      const subject = form.subject || 'Project enquiry';
      const body = [form.message, '', `${form.firstName} ${form.lastName}`.trim(), form.email]
        .filter(Boolean)
        .join('\n');
      return `mailto:${site.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    });

    const submitForm = async () => {
      submitting.value = true;
      status.value = null;
      try {
        if (!recaptcha) {
          throw new Error('reCAPTCHA is unavailable');
        }
        await recaptcha.recaptchaLoaded();
        const token = await recaptcha.executeRecaptcha('contact');

        const response = await axios.post('/send-email', { ...form, recaptcha: token });
        status.value = { ok: true, message: response.data.message };
      } catch (error) {
        status.value = {
          ok: false,
          message: error.response?.data?.error || 'We could not send your message.',
        };
      } finally {
        submitting.value = false;
      }
    };

    return { form, status, submitting, submitForm, mailtoFallback, email: site.email };
  },
};
</script>

<style scoped>
.input-field {
  width: 100%;
  padding: 0.625rem 0.75rem;
  border: 2px solid #ddd;
  border-radius: 0.375rem;
}

.input-field:focus {
  outline: none;
  border-color: #2563eb;
}
</style>
